import type { TFunction } from 'i18next';
import { PayrollSalaryBaseMode } from '../../../../models/general';

/**
 * 🔴 CO-163 / CO-244 — ONE SENTENCE FOR "THE COMPANY IS LEFT WITH NO STATUTORY BASIC WAGE",
 * shared by every control that can cause it.
 *
 * WHY THIS IS A SHARED FUNCTION AND NOT COPY IN EACH DIALOG. There are TWO routes to the zero-flag
 * state — unticking the statutory checkbox in the edit dialog, and deleting the flagged component
 * from the row's trash icon — and they diverged. The edit dialog read the tenant's configured base
 * modes and said "overtime will calculate as ZERO"; the delete dialog never read them at all and
 * showed the soft "the amounts change from the next calculation" copy on a BasicOnly tenant, where
 * the amounts do not change, they become nothing. Same end state, two different promises to HR.
 *
 * `BasicOnly` has NO fallback rung, so with nothing flagged that calculation resolves to 0.
 * `Auto`/`Gross` fall back to the sole component or the full gross, so the amounts change rather
 * than vanish. `null` means the base modes have not loaded — treated as
 * "cannot promise a fallback", which is why it gets the unqualified copy rather than the reassuring
 * one.
 *
 * Three whole sentences rather than a joined fragment list: a translator cannot render
 * "overtime and gratuity" correctly in ar/fr from glued-together words.
 */
export function clearedBasicWageConsequence(
  t: TFunction,
  overtimeBaseMode: PayrollSalaryBaseMode | null,
  eosBaseMode: PayrollSalaryBaseMode | null,
): string {
  const overtimeStrict = overtimeBaseMode === PayrollSalaryBaseMode.BasicOnly;
  const eosStrict = eosBaseMode === PayrollSalaryBaseMode.BasicOnly;

  if (overtimeStrict && eosStrict) {
    return t('payroll:This company calculates BOTH overtime and end-of-service gratuity from the basic salary only, with no fallback. Leaving no component flagged makes both of them calculate as ZERO from the next payroll run.');
  }
  if (overtimeStrict) {
    return t('payroll:This company calculates overtime from the basic salary only, with no fallback. Leaving no component flagged makes overtime calculate as ZERO from the next payroll run.');
  }
  if (eosStrict) {
    return t('payroll:This company calculates end-of-service gratuity from the basic salary only, with no fallback. Leaving no component flagged makes gratuity calculate as ZERO from the next settlement.');
  }
  return t('payroll:Overtime, end of service and pension will fall back to the only salary component, or to the full gross salary. The amounts change from the next calculation.');
}
